import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppleGlyph, Banner, Button, Field, GoogleGlyph } from '@cowoo/shared/ui-web';
import { authErrorKey, emailSchema, mapAuthError } from '@cowoo/shared';
import { OAUTH_REDIRECT_URI, auth } from '../../lib/auth';
import { rememberOtpMethod } from './pending';
import { AuthLayout } from './AuthLayout';

/**
 * `/login` (10-web-admin § 2).
 *
 * The same OtterAuth flow and copy as the member app (09 § 4); what differs is
 * the title and what happens afterwards — `Gate` decides whether this account
 * may see the backoffice at all.
 */
export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [error, setError] = useState<string | null>(
    search.get('error') === 'oauth' ? t('authErrors.oauth_failed', { provider: 'OAuth' }) : null,
  );
  const [busy, setBusy] = useState<'email' | 'apple' | 'google' | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(t('authErrors.email_invalid'));
      return;
    }

    setBusy('email');
    setError(null);
    try {
      const { methodId } = await auth.sendOtp(parsed.data);
      rememberOtpMethod(parsed.data, methodId);
      navigate('/login/code');
    } catch (thrown) {
      const key = authErrorKey(mapAuthError(thrown));
      setError(key ? t(key) : null);
    } finally {
      setBusy(null);
    }
  }

  async function oauth(provider: 'apple' | 'google'): Promise<void> {
    setBusy(provider);
    setError(null);
    try {
      window.location.assign(await auth.startOAuth(provider, OAUTH_REDIRECT_URI));
    } catch {
      setError(t('authErrors.oauth_failed', { provider: provider === 'apple' ? 'Apple' : 'Google' }));
      setBusy(null);
    }
  }

  const disabled = busy !== null;

  return (
    <AuthLayout title={t('admin.login.title')}>
      {/* `ink` for Apple by the HIG, as on the member login and in the app. */}
      <Button
        variant="ink"
        onClick={() => void oauth('apple')}
        loading={busy === 'apple'}
        disabled={disabled}
        full
      >
        <AppleGlyph />
        {t('member.login.apple')}
      </Button>
      <Button
        variant="secondary"
        onClick={() => void oauth('google')}
        loading={busy === 'google'}
        disabled={disabled}
        full
      >
        <GoogleGlyph />
        {t('member.login.google')}
      </Button>

      <div className="flex items-center gap-3 py-2">
        <span className="bg-line h-px flex-1" />
        <span className="text-webSmall text-muted">{t('member.login.divider')}</span>
        <span className="bg-line h-px flex-1" />
      </div>

      <form onSubmit={submit} className="flex flex-col gap-3.5">
        <Field
          label={t('admin.login.email')}
          placeholder={t('member.login.emailPlaceholder')}
          name="email"
          type="email"
          required
          disabled={disabled}
          autoComplete="email"
        />
        <Button type="submit" loading={busy === 'email'} disabled={disabled} full>
          {t('admin.login.submit')}
        </Button>
        {error ? <Banner tone="danger">{error}</Banner> : null}
      </form>
      <p className="text-webSmall text-muted">{t('admin.login.hint')}</p>
    </AuthLayout>
  );
}
